import { WORK_ASSIGNMENT_ACTIVITY_TYPE } from 'constants/activityTypes';

const REJECTED_ASSIGNMENT_STATUS = 'rejected';

const getSequenceCrewMemberId = (sequence) =>
	sequence?.crewMember?.id ??
	sequence?.workAssignments?.find((assignment) => assignment?.crewMember?.id)?.crewMember?.id ??
	null;

const getActivity = (item) => item?.activity ?? {};

const getSequenceRideId = (sequence) => sequence?.rideId ?? sequence?.ride?.id ?? null;

const getActivityRideId = (activity) => activity?.rideId ?? activity?.ride?.id ?? null;
const normalizeStatus = (status) => status?.toLowerCase?.() ?? null;

const matchesSequence = (item, sequence) => {
	const activity = getActivity(item);
	const sequenceGroupId = sequence?.sequenceGroupId;
	const rideId = getSequenceRideId(sequence);

	return Boolean(
		(sequenceGroupId && activity?.sequenceGroupId === sequenceGroupId) ||
			(rideId && getActivityRideId(activity) === rideId)
	);
};

const getRouteTitle = (sequence, existingActivity) =>
	existingActivity?.title ??
	[sequence?.fromStation?.code, sequence?.toStation?.code].filter(Boolean).join(' → ');

const mergeStation = (existingStation, sequenceStation) => ({
	...(existingStation ?? {}),
	...(sequenceStation ?? {}),
});

const shouldHideExtraActivity = (extraActivity, sequence = {}) => {
	const extraStatus = normalizeStatus(extraActivity?.status);

	if (extraStatus !== REJECTED_ASSIGNMENT_STATUS) return false;

	const parentStatus = normalizeStatus(sequence?.status);
	return parentStatus !== REJECTED_ASSIGNMENT_STATUS;
};

const getVisibleExtraActivities = (extraActivities = [], sequence = {}) =>
	extraActivities
		.filter((extraActivity) => !shouldHideExtraActivity(extraActivity, sequence))
		.map((extraActivity) => ({
			...extraActivity,
			sequenceGroupId: extraActivity?.sequenceGroupId ?? sequence?.sequenceGroupId,
		}));

export const sequenceToActivityItem = (sequence, existingItem = {}) => {
	const existingActivity = getActivity(existingItem);

	return {
		...existingItem,
		fromTimestamp: sequence?.fromTimestamp ?? existingItem?.fromTimestamp,
		toTimestamp: sequence?.toTimestamp ?? existingItem?.toTimestamp,
		activity: {
			...existingActivity,
			__typename: existingActivity?.__typename ?? WORK_ASSIGNMENT_ACTIVITY_TYPE,
			title: getRouteTitle(sequence, existingActivity),
			status: sequence?.status ?? existingActivity?.status,
			activityType: sequence?.activityType ?? existingActivity?.activityType,
			rideId: getSequenceRideId(sequence) ?? getActivityRideId(existingActivity),
			sequenceGroupId: sequence?.sequenceGroupId ?? existingActivity?.sequenceGroupId,
			shiftId: sequence?.shiftId ?? existingActivity?.shiftId ?? null,
			fromStation: mergeStation(existingActivity?.fromStation, sequence?.fromStation),
			toStation: mergeStation(existingActivity?.toStation, sequence?.toStation),
			run: sequence?.run ?? existingActivity?.run,
			job: sequence?.job ?? existingActivity?.job,
			extraActivities: getVisibleExtraActivities(sequence?.extraActivities ?? [], {
				...sequence,
				sequenceGroupId: sequence?.sequenceGroupId ?? existingActivity?.sequenceGroupId,
			}),
		},
	};
};

export const replaceSequenceInDriver = (driver, sequence) => {
	const activitiesV2 = driver?.activitiesV2 ?? [];
	let didPatch = false;

	const nextActivities = activitiesV2.map((item) => {
		if (!matchesSequence(item, sequence)) return item;

		didPatch = true;
		return sequenceToActivityItem(sequence, item);
	});

	if (!didPatch) return { driver, didPatch: false };

	return {
		driver: {
			...driver,
			activitiesV2: nextActivities,
		},
		didPatch: true,
	};
};

export const replaceSequenceInTimelineResults = (results = [], sequence) => {
	const crewMemberId = getSequenceCrewMemberId(sequence);
	let didPatch = false;

	const nextResults = results.map((driver) => {
		if (crewMemberId && driver?.id !== crewMemberId) {
			return driver;
		}

		const patched = replaceSequenceInDriver(driver, sequence);

		if (!patched.didPatch) return driver;

		didPatch = true;
		return patched.driver;
	});

	return {
		results: didPatch ? nextResults : results,
		didPatch,
	};
};
